// src/utils/sanitize.js
/**
 * Utilitaires de validation et sanitization des entrées utilisateur.
 *
 * Objectif : empêcher l'injection de contenu malveillant (HTML, JS, caractères
 * de contrôle) dans les champs texte qui seront stockés en BDD et potentiellement
 * affichés dans d'autres contextes (admin panel, emails, reçus) où l'échappement
 * automatique de React ne s'applique pas.
 *
 * Stratégie : whitelist stricte.
 *   - Noms (prénoms) : lettres unicode (accents inclus), espaces, tirets, apostrophes
 *   - Adresses : lettres unicode, chiffres, ponctuation courante (. , ' - / : ( ) # °)
 *
 * Flag /u : active le mode unicode pour que \p{L}, \p{M}, \p{N} fonctionnent.
 *   - \p{L} = toute lettre unicode (a-z, A-Z, é, à, ñ, etc.)
 *   - \p{M} = marque combinatoire (accent, diacritique)
 *   - \p{N} = chiffre décimal
 */

export const MAX_NAME_LENGTH = 50;
export const MAX_ADDRESS_LENGTH = 200;
export const MIN_ADDRESS_LENGTH = 5;

// Whitelist pour prénoms
const NAME_REGEX = /^[\p{L}\p{M}\s'-]+$/u;

// Whitelist pour adresses (complément d'adresse). Autorise en plus :
// chiffres, point, virgule, slash, deux-points, parenthèses, dièse, degré.
const ADDRESS_REGEX = /^[\p{L}\p{M}\p{N}\s.,'\-/:°()#]+$/u;

/**
 * Normalise une chaîne : trim + réduction des espaces multiples.
 * Ne modifie jamais les caractères eux-mêmes.
 * @param {*} str
 * @returns {string}
 */
export function normalizeWhitespace(str) {
  if (typeof str !== 'string') return '';
  return str.trim().replace(/\s+/g, ' ');
}

/**
 * Valide un prénom selon la whitelist stricte.
 * @param {string} str
 * @returns {boolean}
 */
export function isValidName(str) {
  if (typeof str !== 'string') return false;
  const normalized = normalizeWhitespace(str);
  if (normalized.length === 0 || normalized.length > MAX_NAME_LENGTH) return false;
  return NAME_REGEX.test(normalized);
}

/**
 * Valide une adresse (complément) selon la whitelist stricte.
 * @param {string} str
 * @returns {boolean}
 */
export function isValidAddress(str) {
  if (typeof str !== 'string') return false;
  const normalized = normalizeWhitespace(str);
  if (normalized.length < MIN_ADDRESS_LENGTH || normalized.length > MAX_ADDRESS_LENGTH) return false;
  return ADDRESS_REGEX.test(normalized);
}

/**
 * Prépare une chaîne pour l'envoi backend : trim + normalise whitespace.
 * À appeler APRÈS validation (isValidName / isValidAddress) : la whitelist
 * garantit qu'aucun caractère dangereux n'est présent, donc on ne fait
 * ici qu'une normalisation cosmétique.
 * @param {string} str
 * @returns {string}
 */
export function sanitize(str) {
  return normalizeWhitespace(str);
}
